/*
 * Copyright (C) 2018 BarD Software s.r.o
 */

import type { App, DirectiveBinding } from 'vue';
import ErrorWidget from '@/components/ErrorWidget.vue';
import {SS_KEY_DASHBOARD_FLASH_MESSAGE} from '@/utils/SessionStorageKeys';
import { Deferred } from 'jquery';

import $ from 'jquery';

/**
 * ModelsSwitch manages several selection models.
 * It's useful, if you have several selection mode and only one is active.
 * Generic types stays respectively for key-type and
 * data objects type which are handled by selection models.
 */
export class ModelSwitch<K, T> {
    private currentKey: K;
    private readonly controllers = new Map<K, SelectionModel<T>>();
    constructor(private readonly equals: BinaryPredicate<T>) {}

    public register(key: K, listSelector: string, listItemClass: string, idAttr: string) {
        this.controllers.set(key, new SelectionModel<T>(listSelector, listItemClass, idAttr, this.equals));
    }

    public get(key: K): SelectionModel<T> {
        return this.controllers.get(key)!!;
    }

    public switchTo(key: K) {
        this.currentKey = key;
    }

    public current(): SelectionModel<T> {
        return this.get(this.currentKey);
    }
}

export class SelectionModel<T> {
    private get activeModelId(): string | undefined {
        return this.activeListItem ? this.activeListItem.attr(this.idAttr) : undefined;
    }
    private activeListItem?: JQuery;
    private onChangeListener: Consumer<T | null>;
    private id2modelMap: Fxn<string, T | null>;

    constructor(private readonly listSelector: string,
                private readonly listItemClass: string,
                private readonly idAttr: string,
                private readonly equals: BinaryPredicate<T>) {}

    public init(id2model: Fxn<string, T | undefined>, onChange: Consumer<T>): JQueryDeferred<any> {
        const result = Deferred();
        result.done(() => {
            this.onChangeListener = onChange;
            this.id2modelMap = (x) => {
                const o = id2model(x);
                return o ? o : null;
            };
            this.setActive(this.getActiveModelItem());
        });
        return result;
    }

    public setActiveAsync(newValue: T): JQueryDeferred<any> {
        const result = Deferred();
        result.done(() => {
            this.setActive(newValue);
        });
        return result;
    }

    public setActive(newValue: T | null) {
        const list = $(this.listSelector);

        this.activeListItem = undefined;

        list.find(this.listItemClass).each((_, elem) => {
            const value = this.getValue($(elem));

            if (!value || !newValue) {
                return;
            }

            if (this.equals(value, newValue)) {
                this.setActiveItem($(elem));
                return false;
            }
        });

        if (!this.activeListItem) {
            const newActiveListItem = this.findNewActiveListItem();
            this.setActiveItem(newActiveListItem);
        }

        list.off('click');
        // Problem: bootstrap doesn't propagate an event on dropdown: https://github.com/twbs/bootstrap/issues/25610
        // Consequences: selection of item can't happen and they're applying action to an unselected item
        // Solution: we explicitly use addEventListener and set captureing flag to `true`.
        // This leads to firstly capturing an event for an item, then the event passes to item's children elements.
        list.find(this.listItemClass).on('click', this.onListItemClick);
    }

    public getActiveModelItem(): T | null {
        return this.activeModelId ? this.id2modelMap(this.activeModelId) : null;
    }

    public onListItemClick = (evt: JQuery.TriggeredEvent) => {
        const jqTarget = $(evt.target).closest(this.listItemClass);
        this.setActiveItem(jqTarget);
        return true;
    }

    private findNewActiveListItem() {
        const items = $(this.listSelector).find(this.listItemClass);
        const newActiveListItem = items.filter('.active').first();

        if (newActiveListItem.length === 0) {
            return items.first();
        }

        return newActiveListItem;
    }

    private getValue(jqItem: JQuery): T | null {
        return this.id2modelMap(jqItem.attr(this.idAttr));
    }

    private setActiveItem(targetListItem: JQuery) {
        $(this.listSelector).find(this.listItemClass).removeClass('active');
        this.activeListItem = targetListItem.first();
        this.activeListItem.addClass('active');
        this.onChangeListener(this.getActiveModelItem());
    }
}

let errorWidget: ErrorWidget | undefined;
export function setErrorWidget(widget: ErrorWidget) {
    errorWidget = widget;
}

export function handleXhrError(jqXhr: JQueryXHR, msg: string) {
    // When a .then() callback throws, .fail() receives that Error instead of the XHR.
    if (!isXhrError(jqXhr)) {
        console.error(jqXhr);
        showError(msg);
        return;
    }
    if (errorWidget) {
        errorWidget.handleXhrError(jqXhr, msg);
    }
}

/**
 * Picks the message to show for a failed XHR request: the trimmed response body, if the
 * server sent it as plain text, and the fallback message otherwise. Our own error replies
 * are text/plain, while error pages from the frontend server or the load balancer are HTML
 * and should not be dumped into the error widget.
 */
export function xhrErrorMessage(jqXhr: JQueryXHR, fallback: string): string {
    const contentType = jqXhr.getResponseHeader('Content-Type') || '';
    if (!contentType.startsWith('text/plain')) {
        return fallback;
    }
    return jqXhr.responseText?.trim() || fallback;
}

export function showError(msg: string) {
    if (errorWidget) {
        errorWidget.showError(msg);
    }
}

export function showInfo(msg: string) {
    if (errorWidget) {
        errorWidget.showInfo(msg);
    }
}

/**
 * Saves a base64-encoded project file, as returned by /p/read, via a temporary download link.
 * Projects created from a template may have a filename without the extension, so .gan is appended when missing.
 * The optional suffix goes before the extension in parentheses, e.g. to tell downloaded versions apart.
 */
export function saveProjectFile(base64: string, filename: string, suffix?: string) {
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], {type: 'application/octet-stream'}));
    const a = document.createElement('a');
    a.href = url;
    const basename = filename.replace(/\.gan$/i, '');
    a.download = suffix ? `${basename} (${suffix}).gan` : `${basename}.gan`;
    a.click();
    // Safari may abort the download if the URL is revoked synchronously.
    setTimeout(() => URL.revokeObjectURL(url));
}

export function isXhrError(e: unknown): e is JQuery.jqXHR {
    return typeof e === 'object' && e !== null && 'status' in e && 'statusText' in e;
}

export function setDashboardFlashMessage(message: string): void {
    sessionStorage.setItem(SS_KEY_DASHBOARD_FLASH_MESSAGE, message);
}

export function takeDashboardFlashMessage(): string | null {
    const message = sessionStorage.getItem(SS_KEY_DASHBOARD_FLASH_MESSAGE);
    if (message) {
        sessionStorage.removeItem(SS_KEY_DASHBOARD_FLASH_MESSAGE);
    }
    return message;
}

export const HiddenDirective = {
    mounted(el: HTMLElement, binding: DirectiveBinding) {
        $(el).on('hidden.bs.modal', binding.value);
    },
    unmounted(el: HTMLElement, binding: DirectiveBinding) {
        $(el).off('hidden.bs.modal', binding.value);
    }
};

export function registerUiDirectives(app: App) {
    app.directive('hidden', HiddenDirective as any);
}

export class ShortcutHandler {
    private readonly handlers = new Map<string, Fxn<KeyboardEvent, void>>();

    public register(button: string, handler: Fxn<void, void>) {
        this.handlers[button] = handler;
    }

    public handle(e: KeyboardEvent) {
        if ((e.ctrlKey || e.metaKey) && this.handlers[e.key]) {
            this.handlers[e.key]();
        }
    }
}

export function getOrRequireValue(input: JQuery): number | undefined {
    const value = input.val();
    if (value === '') {
        input.on('animationend', function() {
            input.focus();
            input.off('animationend');
            input.removeClass('invalid').removeClass('animate');
        });
        input.addClass('invalid').addClass('animate');
        return undefined;
    }
    return value as number;
}
